/** Handles enemy spawning, position validation, sector population, and respawn timers. */
import Phaser from 'phaser';
import { Enemy } from '../entities/Enemy';
import { ConfigLoader } from './ConfigLoader';
import type { SectorConfig, SpawnPointConfig, SpawnPointEnemy } from './ConfigLoader';
import type { Position } from '../utils/Types';
import { worldToTile, tileToWorld, distance, randomAngle } from '../utils/Math';
import { tileBlocksMovementStrict } from '../ai/constants';
import type { StatusEffectSystem } from './StatusEffectSystem';
import { DebugLog } from '../debug/DebugLog';

/** Generate a short random 3-char hex tag for stable enemy identification. */
function randomTag(): string {
  return Math.random().toString(16).slice(2, 5).toUpperCase();
}

interface DebugSpawnPoint {
  x: number;
  y: number;
  archetypeId: string;
}

/** Describes an enemy's intended spawn: where to physically appear and where to commute to. */
interface DutySpawn {
  archetypeId: string;
  dutyX: number;
  dutyY: number;
}

/** Tracks a killed enemy for respawn. */
interface RespawnEntry {
  archetypeId: string;
  dutyX: number;
  dutyY: number;
  deathTime: number;
  /** Optional respawn location override — bypasses perimeter spawn when present. */
  respawnX?: number;
  respawnY?: number;
}

/** Callbacks that the spawner wires onto each newly-created enemy. */
export interface SpawnerCallbacks {
  playerSprite: Phaser.GameObjects.GameObject | null;
  buildingBuffers: Phaser.Physics.Arcade.StaticGroup | null;
  projectileSpawner: ((x: number, y: number, tx: number, ty: number, cfg: { speed: number; damage: number; lifetimeMs: number; size: number; color: number; attackType: string }) => void) | null;
  backupCallback: ((originX: number, originY: number, radius: number) => void) | null;
  corpseProvider: (() => readonly import('./EnemyManager').CorpseRecord[]) | null;
  statusEffectSystem: StatusEffectSystem | null;
  onEnemyDied: (enemy: Enemy) => void;
}

/** Manages enemy spawning, position validation, sector population, and respawn timers. */
export class EnemySpawner {
  private scene: Phaser.Scene;
  private groundLayer: Phaser.Tilemaps.TilemapLayer;
  private enemies: Enemy[];
  private callbacks: SpawnerCallbacks;

  // Perimeter spawn points (precomputed on init)
  private perimeterPoints: Position[] = [];

  // Respawn queue
  private respawnQueue: RespawnEntry[] = [];
  private respawnTimerMs: number;
  private maxActive: number;

  constructor(scene: Phaser.Scene, groundLayer: Phaser.Tilemaps.TilemapLayer, enemies: Enemy[], callbacks: SpawnerCallbacks) {
    this.scene = scene;
    this.groundLayer = groundLayer;
    this.enemies = enemies;
    this.callbacks = callbacks;
    const spawnerConfig = ConfigLoader.getSpawnerConfig();
    this.respawnTimerMs = spawnerConfig.defaultRespawnTimerMs;
    this.maxActive = spawnerConfig.defaultMaxActive;
    this.discoverPerimeterSpawnPoints();
  }

  // --- Perimeter spawn point discovery ---

  /** Find walkable tiles along the map edges for off-screen enemy spawning. */
  private discoverPerimeterSpawnPoints(): void {
    const map = this.groundLayer.tilemap;
    const w = map.width;
    const h = map.height;
    const points: Position[] = [];

    // Scan map edges: top row, bottom row, left column, right column
    for (let x = 0; x < w; x++) {
      this.tryAddPerimeterTile(x, 0, points);
      this.tryAddPerimeterTile(x, h - 1, points);
    }
    for (let y = 1; y < h - 1; y++) {
      this.tryAddPerimeterTile(0, y, points);
      this.tryAddPerimeterTile(w - 1, y, points);
    }

    // If no edge tiles are walkable, scan a few tiles inward
    if (points.length === 0) {
      for (let x = 2; x < w - 2; x += 4) {
        this.tryAddPerimeterTile(x, 2, points);
        this.tryAddPerimeterTile(x, h - 3, points);
      }
      for (let y = 2; y < h - 2; y += 4) {
        this.tryAddPerimeterTile(2, y, points);
        this.tryAddPerimeterTile(w - 3, y, points);
      }
    }

    this.perimeterPoints = points;
  }

  private tryAddPerimeterTile(tx: number, ty: number, out: Position[]): void {
    const tile = this.groundLayer.getTileAt(tx, ty);
    if (!tileBlocksMovementStrict(tile)) {
      out.push({ x: tileToWorld(tx), y: tileToWorld(ty) });
    }
  }

  /** Pick the perimeter spawn point closest to a duty position. */
  private getSpawnPointForDuty(dutyX: number, dutyY: number): Position | null {
    if (this.perimeterPoints.length === 0) return null;

    let best: Position | null = null;
    let bestDist = Infinity;
    for (const p of this.perimeterPoints) {
      const d = distance(p.x, p.y, dutyX, dutyY);
      if (d < bestDist) {
        bestDist = d;
        best = p;
      }
    }
    return best;
  }

  // --- Core spawn ---

  /** Spawn a single enemy of the given archetype at the nearest walkable tile to the given world coords. */
  spawnAt(x: number, y: number, archetypeId: string): Enemy | null {
    const config = ConfigLoader.getEnemy(archetypeId);
    if (!config) {
      console.warn(`EnemySpawner: unknown archetype "${archetypeId}"`);
      return null;
    }

    // Validate spawn position — find nearest walkable tile if target is blocked
    const safePos = this.findWalkablePosition(x, y);
    if (!safePos) {
      console.warn(`EnemySpawner: no walkable tile near (${x},${y}) for "${archetypeId}"`);
      return null;
    }

    const tag = randomTag();
    const enemy = new Enemy(this.scene, safePos.x, safePos.y, config, this.groundLayer, tag);
    enemy.instanceTag = tag;

    // Collide with tilemap
    this.scene.physics.add.collider(enemy.sprite, this.groundLayer);

    // Collide with player (if registered)
    if (this.callbacks.playerSprite) {
      this.scene.physics.add.collider(this.callbacks.playerSprite, enemy.sprite);
    }

    // Collide with building wall buffers (if registered)
    if (this.callbacks.buildingBuffers) {
      this.scene.physics.add.collider(enemy.sprite, this.callbacks.buildingBuffers);
    }

    // Wire projectile spawner if available
    if (this.callbacks.projectileSpawner) {
      enemy.setProjectileSpawner(this.callbacks.projectileSpawner);
    }

    // Wire backup callback if available
    if (this.callbacks.backupCallback) {
      enemy.setBackupCallback(this.callbacks.backupCallback);
    }

    // Wire corpse provider if available
    if (this.callbacks.corpseProvider) {
      enemy.ai.setCorpseProvider(this.callbacks.corpseProvider);
    }

    // Create per-entity status effect tracker if system is available
    if (this.callbacks.statusEffectSystem) {
      enemy.statusEffectTracker = this.callbacks.statusEffectSystem.createTracker();
    }

    // Randomize initial facing so groups don't all face south
    const angle = randomAngle();
    enemy.ai.setFacing(Math.cos(angle), Math.sin(angle));

    // Mark for deferred removal when HP reaches zero (any source: combat, knockback, environmental)
    enemy.events.once('entity-died', () => {
      this.callbacks.onEnemyDied(enemy);
    });

    this.enemies.push(enemy);
    return enemy;
  }

  /** Find a walkable tile-center position at or near the given world coords. Spirals outward up to 10 tiles. */
  private findWalkablePosition(worldX: number, worldY: number): Position | null {
    const tx = worldToTile(worldX);
    const ty = worldToTile(worldY);

    // Check the target tile first
    const tile = this.groundLayer.getTileAt(tx, ty);
    if (!tileBlocksMovementStrict(tile)) {
      return { x: tileToWorld(tx), y: tileToWorld(ty) };
    }

    // Spiral outward to find the nearest walkable tile
    for (let radius = 1; radius <= 10; radius++) {
      for (let dy = -radius; dy <= radius; dy++) {
        for (let dx = -radius; dx <= radius; dx++) {
          if (Math.abs(dx) !== radius && Math.abs(dy) !== radius) continue; // only check perimeter
          const nx = tx + dx;
          const ny = ty + dy;
          const neighbor = this.groundLayer.getTileAt(nx, ny);
          if (!tileBlocksMovementStrict(neighbor)) {
            return { x: tileToWorld(nx), y: tileToWorld(ny) };
          }
        }
      }
    }

    return null;
  }

  // --- Population from sector config ---

  /** Spawn the initial enemy population from a sector config's spawn points. */
  populateFromSector(sectorConfig: SectorConfig): void {
    this.respawnTimerMs = sectorConfig.respawnTimerMs;
    this.maxActive = sectorConfig.maxActive;

    DebugLog.log('spawn', `populateFromSector: ${sectorConfig.spawnPoints.length} spawn points, maxActive=${this.maxActive}`);
    for (const sp of sectorConfig.spawnPoints) {
      DebugLog.log('spawn', `  spawn point "${sp.id}" at (${sp.x},${sp.y}) — ${sp.enemies.length} enemies` +
        (sp.respawnX !== undefined ? ` respawn=(${sp.respawnX},${sp.respawnY})` : ''));
      for (const enemyDef of sp.enemies) {
        this.spawnFromPoint(sp, enemyDef);
      }
    }
  }

  /**
   * Apply jitter, spawn an enemy at the resulting position, set its duty post, and return it.
   * Returns null if no walkable tile was found near the jittered coords.
   */
  private spawnWithDuty(baseX: number, baseY: number, archetypeId: string, dutyX: number, dutyY: number, jitterRange: number): Enemy | null {
    const jitterX = (Math.random() - 0.5) * jitterRange;
    const jitterY = (Math.random() - 0.5) * jitterRange;
    const enemy = this.spawnAt(baseX + jitterX, baseY + jitterY, archetypeId);
    if (!enemy) return null;
    enemy.ai.setDutyPosition(dutyX, dutyY);
    return enemy;
  }

  /**
   * Spawn a single enemy using the spawn-point fallback chain:
   * 1. Enemy spawnX/spawnY → exact coords, snapped to nearest walkable tile
   * 2. Spawn point x/y → snapped to nearest walkable tile
   * Adds ±32px jitter to prevent stacking. Sets duty post and respawn metadata.
   */
  private spawnFromPoint(sp: SpawnPointConfig, enemyDef: SpawnPointEnemy): Enemy | null {
    // Resolve initial spawn position via fallback chain
    const baseX = enemyDef.spawnX ?? sp.x;
    const baseY = enemyDef.spawnY ?? sp.y;

    const { pointSpawnJitterPx } = ConfigLoader.getSpawnerConfig();
    const enemy = this.spawnWithDuty(baseX, baseY, enemyDef.archetypeId, enemyDef.dutyX, enemyDef.dutyY, pointSpawnJitterPx);
    if (!enemy) {
      DebugLog.log('spawn', `    FAILED ${enemyDef.archetypeId} — no walkable tile near (${Math.round(baseX)},${Math.round(baseY)})`);
      return null;
    }

    // Resolve respawn metadata via fallback chain:
    // 1. Per-enemy respawnX/respawnY
    // 2. Group-level respawnX/respawnY
    // 3. null (perimeter fallback at death time)
    const respawnX = enemyDef.respawnX ?? sp.respawnX;
    const respawnY = enemyDef.respawnY ?? sp.respawnY;
    if (respawnX !== undefined || respawnY !== undefined) {
      enemy.respawnMeta = { respawnX, respawnY };
    }

    const src = enemyDef.spawnX !== undefined ? 'enemy-override' : 'spawn-point';
    DebugLog.log('spawn', `    [${enemy.instanceTag}] ${enemyDef.archetypeId} at (${Math.round(enemy.body.center.x)},${Math.round(enemy.body.center.y)}) src=${src} duty=(${enemyDef.dutyX},${enemyDef.dutyY})` +
      (enemy.respawnMeta ? ` respawn=(${respawnX},${respawnY})` : ' respawn=perimeter'));

    return enemy;
  }

  /** Spawn a single enemy at a perimeter point, setting its duty post to the intended position. */
  private spawnAtPerimeter(spawn: DutySpawn): Enemy | null {
    const spawnPoint = this.getSpawnPointForDuty(spawn.dutyX, spawn.dutyY);
    if (!spawnPoint) {
      // Fallback: spawn directly at duty position
      return this.spawnAt(spawn.dutyX, spawn.dutyY, spawn.archetypeId);
    }

    // Random offset prevents multiple enemies from stacking at the same perimeter point
    const { perimeterOffsetPx } = ConfigLoader.getSpawnerConfig();
    const offsetX = (Math.random() - 0.5) * perimeterOffsetPx;
    const offsetY = (Math.random() - 0.5) * perimeterOffsetPx;
    const enemy = this.spawnAt(spawnPoint.x + offsetX, spawnPoint.y + offsetY, spawn.archetypeId);
    if (enemy) {
      // Reassign duty position — enemy will commute via return-to-post
      enemy.ai.setDutyPosition(spawn.dutyX, spawn.dutyY);
    }
    return enemy;
  }

  /** Spawn a batch of enemies from a list of spawn point descriptors. */
  spawnGroup(spawns: DebugSpawnPoint[]): void {
    for (const spawn of spawns) {
      this.spawnAt(spawn.x, spawn.y, spawn.archetypeId);
    }
  }

  /** Spawn the default MVP enemy layout near the player's current position. */
  spawnNearPlayer(playerX: number, playerY: number): void {
    const spawns: DebugSpawnPoint[] = [
      { x: playerX + 320, y: playerY - 64, archetypeId: 'grunt' },
    ];
    this.spawnGroup(spawns);
  }

  // --- Respawn system ---

  /** Track a killed enemy for future respawn. Optional respawn coords override perimeter spawn. */
  queueRespawn(archetypeId: string, dutyX: number, dutyY: number, respawnX?: number, respawnY?: number): void {
    this.respawnQueue.push({
      archetypeId,
      dutyX,
      dutyY,
      deathTime: Date.now(),
      respawnX,
      respawnY,
    });
    DebugLog.log('spawn', `QUEUED ${archetypeId} for respawn in ${(this.respawnTimerMs / 1000).toFixed(0)}s duty=(${dutyX},${dutyY})` +
      (respawnX !== undefined ? ` respawn=(${respawnX},${respawnY})` : ' respawn=perimeter'));
  }

  /** Check respawn timers and spawn replacements at perimeter points. Called from update(). */
  processRespawns(): void {
    if (this.respawnQueue.length === 0) return;
    if (this.enemies.length >= this.maxActive) return;

    const now = Date.now();
    const ready: RespawnEntry[] = [];
    const remaining: RespawnEntry[] = [];

    for (const entry of this.respawnQueue) {
      if (now - entry.deathTime >= this.respawnTimerMs) {
        ready.push(entry);
      } else {
        remaining.push(entry);
      }
    }

    this.respawnQueue = remaining;

    for (const entry of ready) {
      if (this.enemies.length >= this.maxActive) {
        // Re-queue entries we can't spawn yet
        this.respawnQueue.push(entry);
      } else if (entry.respawnX !== undefined && entry.respawnY !== undefined) {
        // Respawn at override coords with jitter
        const { pointSpawnJitterPx } = ConfigLoader.getSpawnerConfig();
        const enemy = this.spawnWithDuty(entry.respawnX, entry.respawnY, entry.archetypeId, entry.dutyX, entry.dutyY, pointSpawnJitterPx);
        if (enemy) {
          enemy.respawnMeta = { respawnX: entry.respawnX, respawnY: entry.respawnY };
          DebugLog.log('spawn', `RESPAWN [${enemy.instanceTag}] ${entry.archetypeId} at override (${Math.round(enemy.body.center.x)},${Math.round(enemy.body.center.y)}) duty=(${entry.dutyX},${entry.dutyY})`);
        }
      } else {
        const enemy = this.spawnAtPerimeter(entry);
        if (enemy) {
          DebugLog.log('spawn', `RESPAWN [${enemy.instanceTag}] ${entry.archetypeId} at perimeter (${Math.round(enemy.body.center.x)},${Math.round(enemy.body.center.y)}) duty=(${entry.dutyX},${entry.dutyY})`);
        }
      }
    }
  }

  /** Clear respawn queue. */
  clearQueue(): void {
    this.respawnQueue = [];
  }
}
